<template>
  <div class="main">
    <!-- 背景装饰 -->
    <div class="bg-decor">
      <div class="circle circle-1"></div>
      <div class="circle circle-2"></div>
      <div class="circle circle-3"></div>
      <div class="blob blob-1"></div>
      <div class="blob blob-2"></div>
    </div>

    <!-- 左侧品牌区 -->
    <div class="brand">
      <div class="brand-inner">
        <div class="logo-wrap">
          <div class="logo-icon">🌿</div>
        </div>
        <h1 class="brand-title">智慧后勤管理系统</h1>
        <p class="brand-subtitle">Smart Logistics Management System</p>
        <div class="brand-tags">
          <span class="tag">高效</span>
          <span class="tag">智能</span>
          <span class="tag">便捷</span>
        </div>
        <ul class="brand-features">
          <li>📊 数据可视化 · 实时掌控</li>
          <li>🍽️ 食堂菜单 · 一键发布</li>
          <li>💳 支付订单 · 智能对账</li>
          <li>👥 用户管理 · 权限分明</li>
        </ul>
      </div>
    </div>

    <!-- 右侧登录框 -->
    <div class="loginBox">
      <div class="login-card">
        <div class="card-header">
          <h2 class="title">欢迎登录</h2>
          <p class="minTitle">后台管理系统</p>
        </div>

        <el-form
          class="form"
          :rules="rules"
          ref="loginFormRef"
          :model="user"
          @submit.prevent="login"
        >
          <el-form-item prop="username">
            <el-input
              prefix-icon="User"
              placeholder="请输入系统账号"
              v-model="user.username"
              size="large"
            />
          </el-form-item>
          <el-form-item prop="password">
            <el-input
              show-password
              prefix-icon="Lock"
              placeholder="请输入系统密码"
              v-model="user.password"
              size="large"
              @keyup.enter="login"
            />
          </el-form-item>
          <el-form-item>
            <el-button
              type="success"
              native-type="submit"
              size="large"
              :loading="loading"
              class="login-btn"
            >
              登 录
            </el-button>
          </el-form-item>
        </el-form>

        <div class="card-footer">
          <span>© 2026 智慧后勤 · 版权所有</span>
        </div>
      </div>
    </div>
  </div>
</template>

<script setup>
import { ref } from 'vue'
import { useRouter } from 'vue-router'
import { ElMessage, ElLoading } from 'element-plus'
import axios from 'axios'

const router = useRouter()

// 确保 axios 的基础路径为空，生产环境由 Nginx 代理
axios.defaults.baseURL = ''

const user = ref({
  username: '',
  password: ''
})

const loading = ref(false)
const loginFormRef = ref(null)

const rules = ref({
  username: [
    { required: true, message: '请输入系统账号', trigger: 'blur' }
  ],
  password: [
    { required: true, message: '请输入系统密码', trigger: 'blur' }
  ]
})

const login = async () => {
  loginFormRef.value.validate(async (valid) => {
    if (!valid) return

    loading.value = true
    const loadingService = ElLoading.service({
      lock: true,
      text: '登录中...',
      background: 'rgba(255, 255, 255, 0.7)'
    })

    try {
      // 1. 请求登录接口获取 Token
      const loginRes = await axios.post(
        '/api/auth/external/login/',
        {
          username: user.value.username,
          password: user.value.password
        },
        {
          headers: { 'Content-Type': 'application/json' },
          timeout: 10000
        }
      )

      const res = loginRes.data
      if (res.code !== 200) {
        ElMessage.error(res.msg || '登录失败')
        return
      }

      // 2. 保存 Token
      const token = 'Bearer ' + res.data.access
      localStorage.setItem('adminToken', token)
      localStorage.setItem('tokenStartTime', Math.round(Date.now() / 1000).toString())

      // 3. 获取当前用户详细信息
      const userRes = await axios.get('/api/auth/external/owner/', {
        headers: { Authorization: token }
      })

      if (!userRes.data.data) {
        throw new Error('用户信息为空')
      }
      const userInfo = userRes.data.data

      // 4. 保存用户信息
      localStorage.setItem('adminName', userInfo.showname || '')
      localStorage.setItem('userId', userInfo.id || '')
      
      const roles = userInfo.roles || ['normal']
      localStorage.setItem('roles', JSON.stringify(roles))
      
      // ===== 核心修复：写入超级管理员标识 =====
      // 如果你的后端返回的角色叫 'superuser'、'admin' 或者 'staff'，在这里判断
      if (roles.includes('superuser') || roles.includes('admin')) {
          localStorage.setItem('isSuperuser', 'true')
      } else {
          localStorage.setItem('isSuperuser', 'false')
      }

      ElMessage.success('登录成功')
      // 跳转到首页
      router.push('/home').catch(err => err)

    } catch (err) {
      // 错误处理：优先获取后端返回的错误信息
      const errorMsg = err.response?.data?.message || err.response?.data?.detail || err.message || '登录失败'
      ElMessage.error(errorMsg)
      console.error('登录流程错误:', err)
    } finally {
      loading.value = false
      loadingService.close()
    }
  })
}
</script>

<style lang="less" scoped>
.main {
  width: 100vw;
  height: 100vh;
  margin: 0;
  padding: 0;
  display: flex;
  align-items: center;
  justify-content: center;
  position: relative;
  overflow: hidden;
  background: linear-gradient(135deg, #e8f5e9 0%, #c8e6c9 50%, #a5d6a7 100%);
}

/* ===== 背景装饰 ===== */
.bg-decor {
  position: absolute;
  inset: 0;
  overflow: hidden;
  z-index: 0;
}

.circle {
  position: absolute;
  border-radius: 50%;
  background: rgba(76, 175, 80, 0.15);
  animation: float 8s ease-in-out infinite;
}

.circle-1 {
  width: 300px;
  height: 300px;
  top: -80px;
  left: -80px;
  animation-delay: 0s;
}

.circle-2 {
  width: 200px;
  height: 200px;
  bottom: -60px;
  right: -60px;
  background: rgba(129, 199, 132, 0.2);
  animation-delay: 2s;
}

.circle-3 {
  width: 150px;
  height: 150px;
  top: 40%;
  right: 10%;
  background: rgba(56, 142, 60, 0.1);
  animation-delay: 4s;
}

.blob {
  position: absolute;
  border-radius: 40% 60% 70% 30% / 40% 50% 60% 50%;
  background: rgba(76, 175, 80, 0.08);
  animation: morph 12s ease-in-out infinite;
}

.blob-1 {
  width: 400px;
  height: 400px;
  top: -100px;
  right: -100px;
}

.blob-2 {
  width: 350px;
  height: 350px;
  bottom: -120px;
  left: -100px;
  background: rgba(129, 199, 132, 0.1);
  animation-delay: 3s;
}

@keyframes float {
  0%, 100% { transform: translateY(0) scale(1); }
  50% { transform: translateY(-20px) scale(1.05); }
}

@keyframes morph {
  0%, 100% { border-radius: 40% 60% 70% 30% / 40% 50% 60% 50%; }
  50% { border-radius: 60% 40% 30% 70% / 50% 60% 40% 50%; }
}

/* ===== 左侧品牌区 ===== */
.brand {
  flex: 1;
  display: flex;
  align-items: center;
  justify-content: center;
  position: relative;
  z-index: 1;
  padding: 0 60px;
}

.brand-inner {
  max-width: 480px;
}

.logo-wrap {
  width: 80px;
  height: 80px;
  border-radius: 24px;
  background: linear-gradient(135deg, #66bb6a 0%, #43a047 100%);
  display: flex;
  align-items: center;
  justify-content: center;
  margin-bottom: 32px;
  box-shadow: 0 12px 32px rgba(67, 160, 71, 0.3);
}

.logo-icon {
  font-size: 40px;
  line-height: 1;
}

.brand-title {
  font-size: 42px;
  font-weight: 700;
  color: #1b5e20;
  margin: 0 0 12px;
  letter-spacing: 1px;
}

.brand-subtitle {
  font-size: 16px;
  color: #558b2f;
  margin: 0 0 24px;
  letter-spacing: 2px;
  font-weight: 400;
}

.brand-tags {
  display: flex;
  gap: 12px;
  margin-bottom: 36px;
}

.tag {
  padding: 6px 18px;
  background: rgba(76, 175, 80, 0.15);
  color: #2e7d32;
  border-radius: 20px;
  font-size: 14px;
  font-weight: 500;
  border: 1px solid rgba(76, 175, 80, 0.3);
}

.brand-features {
  list-style: none;
  padding: 0;
  margin: 0;
}

.brand-features li {
  font-size: 16px;
  color: #33691e;
  margin-bottom: 16px;
  padding-left: 8px;
  line-height: 1.6;
}

/* ===== 右侧登录卡片 ===== */
.loginBox {
  flex: 0 0 480px;
  display: flex;
  align-items: center;
  justify-content: center;
  position: relative;
  z-index: 1;
  padding-right: 260px;
}

.login-card {
  width: 420px;
  background: rgba(255, 255, 255, 0.95);
  backdrop-filter: blur(20px);
  border-radius: 24px;
  padding: 48px 40px 32px;
  box-shadow:
    0 20px 60px rgba(27, 94, 32, 0.15),
    0 0 0 1px rgba(255, 255, 255, 0.5) inset;
  border: 1px solid rgba(255, 255, 255, 0.8);
}

.card-header {
  text-align: center;
  margin-bottom: 36px;
}

.title {
  font-size: 28px;
  font-weight: 700;
  color: #1b5e20;
  margin: 0 0 8px;
}

.minTitle {
  font-size: 14px;
  color: #81c784;
  margin: 0;
  letter-spacing: 3px;
}

.form {
  :deep(.el-input__wrapper) {
    border-radius: 12px;
    box-shadow: 0 0 0 1px #e0e0e0 inset;
    padding: 6px 16px;
    transition: all 0.3s;
  }

  :deep(.el-input__wrapper:hover) {
    box-shadow: 0 0 0 1px #a5d6a7 inset;
  }

  :deep(.el-input__wrapper.is-focus) {
    box-shadow: 0 0 0 2px #66bb6a inset;
  }

  :deep(.el-form-item) {
    margin-bottom: 24px;
  }
}

.login-btn {
  width: 100%;
  height: 48px;
  border-radius: 12px;
  font-size: 16px;
  font-weight: 600;
  letter-spacing: 4px;
  background: linear-gradient(135deg, #66bb6a 0%, #43a047 100%);
  border: none;
  box-shadow: 0 8px 20px rgba(67, 160, 71, 0.35);
  transition: all 0.3s;
}

.login-btn:hover {
  transform: translateY(-2px);
  box-shadow: 0 12px 28px rgba(67, 160, 71, 0.45);
}

.card-footer {
  text-align: center;
  margin-top: 24px;
  font-size: 12px;
  color: #a5d6a7;
}

/* ===== 响应式 ===== */
@media (max-width: 1024px) {
  .brand {
    display: none;
  }
  .loginBox {
    flex: 1;
    padding: 20px;
    justify-content: center;
  }
  .login-card {
    width: 100%;
    max-width: 420px;
  }
}
</style>